/**
 * Typed event bus for loosely coupled 3D interaction events.
 *
 * Uses mitt (200 bytes gzipped) to decouple the 3D viewer,
 * drag controllers, and UI components. Consumers subscribe
 * via `emitter.on()` and clean up via `emitter.off()`.
 */
import mitt from "mitt";

export type WallId = "front" | "back" | "left" | "right" | "divider";

export type BuilderEvents = {
	"wall:click": { wall: WallId; xMm: number; yMm: number };
	"opening:hover": {
		nodeId: string;
		position: { x: number; y: number };
	} | null;
	"grid:click": { x: number; z: number };
	"drag:start": { widthMm: number; heightMm: number };
	"drag:move": {
		valid: boolean;
		wall: WallId | null;
		xMm: number;
		yMm: number;
	};
	"drag:end": { placed: boolean; wall?: WallId; xMm?: number; yMm?: number };
	"drag:cancel": void;
	/**
	 * True while the partition HTML handle (PartitionDragAffordance) owns a
	 * drag, false when it commits/cancels. Lets ViewerMeasurementController
	 * stand its pointer handlers down for the duration so the two pointer
	 * owners stay mutually exclusive.
	 */
	"partition:handleDrag": boolean;
	/**
	 * Same contract as 'partition:handleDrag', but for the door/window HTML
	 * pill handles (OpeningChevron) — true while a pill owns an opening move
	 * drag, false when it commits/cancels.
	 */
	"opening:handleDrag": boolean;
	/**
	 * Same contract again, for the whole-building rotation handle
	 * (RotationDragAffordance).
	 */
	"rotation:handleDrag": boolean;
};

export const emitter = mitt<BuilderEvents>();
